/* eslint-disable @typescript-eslint/no-explicit-any */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-button';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import './treasury-pending-approval.template';

export enum Status {
    Pending = 'Pending Approval',
    PendingApproval = 'PendingApproval',
    ApprovalRejected = 'ApprovalRejected',
    ApprovalRejected2 = 'Approval Rejected',
    Approved = 'Approved',
    Active = 'Active',
    Rejected = 'Rejected',
    Canceled = 'Canceled',
    Cancelled = 'Cancelled',
    Expired = 'Expired',
    Failed = 'Failed',
    Completed = 'Completed',
    Ready = 'Ready',
    Scheduled = 'Scheduled',
    Submitted = 'Submitted',
    Initiated = 'Initiated',
    PendingProcess = 'Pending Process',
    Transmitted = 'Transmitted',
}

export const tagName = 'treasury-status-column';
@customElement(tagName)
export class TreasuryStatusColumnTemplate extends TmBaseComponent {
    @property({ type: Array })
    public approvals = [] as any;

    @property({ type: Object })
    public item = {} as any;

    @property({ attribute: false })
    public eligibleApproversFunction?: () => Promise<string[]>;

    @property({ attribute: false })
    public completedApproversFunction?: () => Promise<string[]>;

    @property({ type: Boolean })
    public eligibleApproversVisible = true;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    @state()
    private loadingApprovers = false;

    private get numberOfApprovalsNeeded() {
        return this.item?.numberOfApprovalsNeeded ?? 0;
    }

    private get numberOfApprovalsCompleted() {
        return this.item?.completedApprovalCount
            ? this.item.completedApprovalCount
            : this.approvals.length;
    }

    private get isPendingApproval() {
        return this.item?.status === Status.PendingApproval || this.item?.status === Status.Pending;
    }

    private get statusText() {
        if (this.isPendingApproval) {
            return 'Pending Approval';
        }
        if (this.item?.status === Status.ApprovalRejected) {
            return 'Approval Rejected';
        }
        return this.item?.status;
    }

    private get positiveState() {
        return (
            this.item?.status === Status.Scheduled ||
            this.item?.status === Status.Approved ||
            this.item?.status === Status.Completed ||
            this.item?.status === Status.Ready ||
            this.item?.status === Status.Submitted ||
            this.item?.status === Status.Initiated ||
            this.item?.status === Status.Active ||
            this.item?.status === Status.Transmitted
        );
    }

    private get negativeState() {
        return (
            this.item?.status === Status.Rejected ||
            this.item?.status === Status.Canceled ||
            this.item?.status === Status.Cancelled ||
            this.item?.status === Status.Expired ||
            this.item?.status === Status.ApprovalRejected ||
            this.item?.status === Status.ApprovalRejected2 ||
            this.item?.status === Status.Failed
        );
    }

    private get neutralState() {
        return (
            this.item?.status !== Status.PendingApproval &&
            this.item?.status !== Status.Pending &&
            this.item?.status !== Status.Approved &&
            this.item?.status !== Status.Completed &&
            this.item?.status !== Status.Canceled &&
            this.item?.status !== Status.Expired
        );
    }

    protected render() {
        return html`<div
            @click=${(e: MouseEvent) => this.viewApprovals(e)}
            class=${classMap({
                'flex flex-col items-center justify-center border rounded w-full px-1 overflow-hidden': true,
                'cursor-pointer': this.isPendingApproval,
                'border-attention':
                    this.isPendingApproval ||
                    this.item?.status === Status.Scheduled ||
                    this.item?.status === Status.PendingProcess,
                'border-positive': this.positiveState,
                'border-negative': this.negativeState,
                'border-gray-400': this.neutralState,
            })}
        >
            <div class="flex items-center w-full py-1">
                <div
                    class=${classMap({
                        'min-h-2 min-w-2 rounded-full mr-1': true,
                        'bg-attention':
                            this.isPendingApproval ||
                            this.item?.status === Status.Scheduled ||
                            this.item?.status === Status.PendingProcess,
                        'bg-positive': this.positiveState,
                        'bg-negative': this.negativeState,
                        'bg-gray-400': this.neutralState,
                    })}
                ></div>
                <div class="text-xs text-wrap leading-none">${this.statusText}</div>
            </div>
            ${this.isPendingApproval ? this.renderApprovalPills() : nothing}
        </div>`;
    }

    private renderApprovalPills() {
        const pills = [];
        for (let i = 0; i < this.numberOfApprovalsNeeded; i++) {
            pills.push(this.renderApprovalPill(this.numberOfApprovalsCompleted, i));
        }
        return html`<div class="flex items-center w-full  mt-px mb-1">${pills}</div>`;
    }

    private renderApprovalPill(approval: number, index: number) {
        const isCompleted = index < this.numberOfApprovalsCompleted;
        return html`<div
            class=${classMap({
                'min-h-3 min-w-8 mx-px rounded border border-gray-400': true,
                'bg-mint-150': isCompleted,
                'bg-gray-light-30': !isCompleted,
            })}
        ></div>`;
    }

    private async viewApprovals(event: MouseEvent) {
        if (!this.isPendingApproval) {
            return;
        }
        event.stopPropagation();

        if (this.completedApproversFunction) {
            this.approvals = await this.completedApproversFunction();
        }
        await this.dialogService.open<void>(
            () =>
                html`<treasury-pending-approval-template
                    .approvalItem=${this.item}
                    .approvals=${this.approvals}
                    .completedApprovalCount=${this.numberOfApprovalsCompleted}
                    .eligibleApproversFunction=${this.eligibleApproversFunction}
                    @eligible-approvers-visible=${(e: CustomEvent) => {
                        this.eligibleApproversVisible = e.detail;
                    }}
                    .eligibleApproversVisible=${this.eligibleApproversVisible}
                    class="pb-2"
                    .loading=${this.loadingApprovers}
                ></treasury-pending-approval-template>`,
            `Approvals ${this.item.transactionId ? `for ${this.item.transactionId}` : ''}`,
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Close',
                    },
                },
            }
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TreasuryStatusColumnTemplate;
    }
}
